/** 我的订单：只展示服务端订单事实，不从灵力流水反推支付状态。 */

import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { LoadingState, PageNotice } from "../../components/AsyncState";
import { Button, PageShell } from "../../components/ui";
import { IconPower, IconRetry } from "../../components/Icons";
import {
  fetchPaymentOrders,
  type PaymentOrderListItem,
  type PaymentOrderListResult,
} from "../../services/payments-api";
import { useResource } from "../../state/resource";
import "../pages.css";

const STATUS_TEXT: Record<PaymentOrderListItem["status"], string> = {
  created: "等待支付",
  paying: "支付处理中",
  paid: "等待到账",
  fulfilled: "已到账",
  closed: "已关闭",
  refunded: "已退款",
};

export function OrdersPage() {
  const data = useResource<PaymentOrderListResult>(
    (signal) => fetchPaymentOrders(signal),
    [],
  );
  const [more, setMore] = useState<PaymentOrderListItem[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const ready = data.state.phase === "ready" ? data.state.data : null;
  const orders = [...(ready?.orders ?? []), ...more];
  const hasMore = orders.length < (ready?.total ?? 0);

  const loadMore = useCallback(() => {
    setLoadingMore(true);
    setMoreFailed(false);
    fetchPaymentOrders(undefined, orders.length)
      .then((page) => setMore((previous) => [...previous, ...page.orders]))
      .catch(() => setMoreFailed(true))
      .finally(() => setLoadingMore(false));
  }, [orders.length]);

  return (
    <PageShell
      icon={<IconPower size={20} />}
      title="我的订单"
      actions={
        data.state.phase === "failed" ? (
          <Button tone="quiet" icon={<IconRetry size={15} />} onClick={data.reload}>
            重试
          </Button>
        ) : undefined
      }
    >
      {data.state.phase === "loading" ? <LoadingState label="正在读取订单" /> : null}

      {data.state.phase === "failed" ? (
        <PageNotice
          scene="relationship"
          error={data.state.error}
          onRetry={data.reload}
          expected={["每笔灵力购买的金额、到账数量、时间与最终状态"]}
        />
      ) : null}

      {ready && orders.length === 0 ? (
        <p className="inline-hint">还没有订单。购买过的灵力会在这里留下记录。</p>
      ) : null}

      {ready && orders.length > 0 ? (
        <>
          <p className="inline-hint">共 {ready.total} 笔，已显示 {orders.length} 笔</p>
          <ul className="record-list">
            {orders.map((order) => (
              <li key={order.id} className="record-list__item">
                <span className="record-list__title">
                  {formatCny(order.priceCents)} · {order.grantedPower ?? "—"} 灵力
                </span>
                <span className="record-list__meta">
                  {STATUS_TEXT[order.status]}
                  {order.characterName ? ` · 给 ${order.characterName}` : ""}
                </span>
                <span className="record-list__meta">
                  {new Date(order.createdAt).toLocaleString("zh-CN")}
                </span>
                {order.status === "paid" || order.status === "paying" ? (
                  <Link to={`?orderId=${encodeURIComponent(order.id)}&paymentResult=unknown`}>确认支付结果</Link>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {hasMore ? (
        <Button tone="quiet" disabled={loadingMore} onClick={loadMore}>
          {loadingMore ? "正在翻更早的…" : "看看更早的订单"}
        </Button>
      ) : null}

      {moreFailed ? <p className="inline-hint">刚才没翻动，再点一次试试。</p> : null}
    </PageShell>
  );
}

function formatCny(cents: number): string {
  return new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency: "CNY",
    minimumFractionDigits: 2,
  }).format(cents / 100);
}
